// Timezone utility functions
// Fixes the UTC offset issue and uses user's timezone preference

/**
 * Get user's timezone from JWT token
 */
export const getUserTimezone = () => {
  try {
    const token = localStorage.getItem('token');
    if (token) {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.timezone || 'America/New_York';
    }
  } catch (e) {
    console.error('Error getting user timezone:', e);
  }
  return 'America/New_York';
};

/**
 * Parse date string from database as UTC
 * FIX: SQLite stores as UTC but JS interprets as local, causing offset
 */
export const parseUTCDate = (dateString) => {
  if (!dateString) return null;
  
  // If already has Z, use as-is
  if (dateString.endsWith('Z')) {
    return new Date(dateString);
  }
  
  // SQLite format: "2024-02-07 05:30:00" (UTC)
  // JavaScript interprets this as LOCAL time, causing offset
  // Solution: Treat it as UTC by appending Z
  const utcString = dateString.replace(' ', 'T') + 'Z';
  return new Date(utcString);
};

/**
 * Format date/time using user's timezone
 */
export const formatDateInUserTZ = (dateInput, options = {}) => {
  const timezone = getUserTimezone();
  const date = typeof dateInput === 'string' ? parseUTCDate(dateInput) : dateInput;
  
  if (!date || isNaN(date.getTime())) return '';
  
  const defaultOptions = {
    timeZone: timezone,
    ...options
  };
  
  return new Intl.DateTimeFormat('en-US', defaultOptions).format(date);
};

/**
 * Format time only (HH:MM:SS AM/PM)
 */
export const formatTimeInUserTZ = (dateInput) => {
  return formatDateInUserTZ(dateInput, {
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: true
  });
};

/**
 * Format date only (MMM DD, YYYY)
 *
 * Handles two input shapes correctly:
 *   1. Date-only string "YYYY-MM-DD" → formatted AS THAT CALENDAR DAY,
 *      no timezone conversion. A worker's pay period start of
 *      "2026-03-09" should always read "Mar 9, 2026" no matter what
 *      timezone the viewer is in.
 *   2. Full timestamp (with time component) → parsed as UTC, displayed
 *      in user's timezone (delegates to formatDateInUserTZ).
 *
 * Why: `new Date("2026-03-09").toLocaleDateString(...)` parses as UTC
 * midnight then prints in browser-local time, which in any negative
 * UTC-offset zone (Eastern, Central, etc.) shifts the displayed day
 * back by one. Bug fix for "Mar 9 → Mar 15" timesheets showing as
 * "Mar 8 → Mar 14" in the UI.
 */
export const formatDateOnly = (dateInput) => {
  // Detect date-only string: "YYYY-MM-DD" with no time component
  if (typeof dateInput === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateInput)) {
    const [y, m, d] = dateInput.split('-').map(Number);
    // Construct as local-time noon to avoid any DST edge cases
    const local = new Date(y, m - 1, d, 12, 0, 0);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(local);
  }

  // Full timestamp — keep timezone-aware behavior
  return formatDateInUserTZ(dateInput, {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

/**
 * Format date and time (MM/DD/YYYY HH:MM AM/PM)
 */
export const formatDateTime = (dateInput) => {
  return formatDateInUserTZ(dateInput, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

/**
 * Format date for input fields (YYYY-MM-DD)
 */
export const formatDateForInput = (dateInput) => {
  const date = typeof dateInput === 'string' ? parseUTCDate(dateInput) : dateInput;
  if (!date || isNaN(date.getTime())) return '';
  
  const timezone = getUserTimezone();
  return new Intl.DateTimeFormat('en-CA', { // en-CA gives YYYY-MM-DD
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(date);
};

/**
 * Get current time in user's timezone
 */
export const getCurrentTimeInUserTZ = () => {
  return new Date();
};

/**
 * How far ahead of UTC a zone is at a given instant, in milliseconds.
 *
 * Computed from `Intl` rather than a fixed number because Connecticut is EST
 * (-5) in winter and EDT (-4) in summer, so a hardcoded offset is wrong for
 * roughly half the year.
 */
const zoneOffsetMs = (date, timeZone) => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone, hour12: false,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  }).formatToParts(date);
  const at = (type) => Number((parts.find((p) => p.type === type) || {}).value);
  // `hour12: false` yields 24 for midnight in some ICU versions rather than 0
  // (measured, not assumed — the same trap server/utils/logTimestamp.js:66
  // documents).
  const asUTC = Date.UTC(at('year'), at('month') - 1, at('day'), at('hour') % 24, at('minute'), at('second'));
  return asUTC - date.getTime();
};

/**
 * Format an instant as the value of a `datetime-local` input — "YYYY-MM-DDTHH:mm"
 * — reading the wall clock in the user's own timezone.
 *
 * `new Date(stored).getHours()` is what this replaces, and it was wrong twice
 * over: it reads a zone-less SQLite timestamp as BROWSER-local (so the field
 * showed a job created at 9pm Eastern as 1am, on the following day), and it
 * ignores the timezone on the user's profile entirely.
 *
 * Pairs with `zonedInputToUTC` below — that is the inverse, and a field whose
 * value went through this one round-trips back to the same instant.
 */
export const formatDateTimeForInput = (dateInput) => {
  const date = typeof dateInput === 'string' ? parseUTCDate(dateInput) : dateInput;
  if (!date || isNaN(date.getTime())) return '';

  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: getUserTimezone(), hour12: false,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
  }).formatToParts(date);
  const at = (type) => (parts.find((p) => p.type === type) || {}).value;

  const hour = String(Number(at('hour')) % 24).padStart(2, '0');
  return `${at('year')}-${at('month')}-${at('day')}T${hour}:${at('minute')}`;
};

/**
 * Turn a `datetime-local` value into a UTC ISO instant.
 *
 * The input carries no zone, so something has to decide what "9:00pm" means.
 * Sent as-is it is the SERVER that decides — `new Date(value).toISOString()`
 * in a route reads it in the server's zone, which is UTC — and an admin in
 * Connecticut typing 9pm gets 5pm stored. Resolve it here instead, against the
 * timezone on their profile, so the instant leaves the browser unambiguous.
 *
 * Returns null for anything that is not a datetime-local value, so a caller
 * can decline to send rather than post a guess.
 */
export const zonedInputToUTC = (value) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(String(value || ''));
  if (!m) return null;

  const [, y, mo, d, h, mi] = m.map(Number);
  const wallAsUTC = Date.UTC(y, mo - 1, d, h, mi);
  if (Number.isNaN(wallAsUTC)) return null;

  const tz = getUserTimezone();
  // Two passes: the offset has to be sampled at an instant, and the only
  // instant available to start with is the wall time read as UTC. That is off
  // by the offset itself, which matters within an hour of a DST transition —
  // resampling at the corrected instant settles it.
  let instant = wallAsUTC - zoneOffsetMs(new Date(wallAsUTC), tz);
  instant = wallAsUTC - zoneOffsetMs(new Date(instant), tz);

  const date = new Date(instant);
  return isNaN(date.getTime()) ? null : date.toISOString();
};

/**
 * Format duration in seconds to HH:MM:SS
 */
export const formatDuration = (seconds) => {
  if (!seconds || seconds < 0) return '00:00:00';
  
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  
  return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

/**
 * Format duration in seconds to readable format (Xh Ym)
 */
export const formatDurationReadable = (seconds) => {
  if (!seconds || seconds < 0) return '0h 0m';
  
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  return `${hrs}h ${mins}m`;
};

/**
 * Format duration as decimal hours
 */
export const formatDurationAsHours = (seconds) => {
  if (!seconds || seconds < 0) return 0;
  return (seconds / 3600).toFixed(2);
};
